import React, { useState, useEffect, useCallback } from 'react';
import config from '../config';
import {
  Box,
  Paper,
  Typography,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Alert,
  Avatar,
  CircularProgress,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  PersonOff as PersonOffIcon,
  Person as PersonIcon,
  Lock as LockIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { usersAPI as userAPI, type User, type UserSearchParams } from '../services';
import EmptyState from '../components/EmptyState';
import { useAuth } from '../context/AuthContext';
import { isSuperAdmin, hasPermission, getFilterableUserTypes } from '../utils/userPermissions';
import { getUserTypeLabel, getUserTypeColor } from '../utils/userTypes';
import {
  HEADER_CELL_SX,
  ListToolbar,
  NameCell,
  StatusDot,
  RowActionsMenu,
  DetailStrip,
  SelectableTableRow,
  SelectableCard,
  useRowSelection,
  type RowAction,
} from '../components/ListPageShared';

const AVATAR_COLORS = ['#79A82A', '#2196f3', '#f44336', '#ff9800', '#9c27b0', '#00bcd4', '#e91e63', '#4caf50'];

type StatusFilterKey = 'all' | 'active' | 'inactive';

const UsersPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const userIsSuper = isSuperAdmin(currentUser);
  const userCanCreate = hasPermission(currentUser, 'users.create');
  const userCanManage = hasPermission(currentUser, 'users.edit');
  const filterableTypes = getFilterableUserTypes(currentUser);

  // State management
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [snackbar, setSnackbar] = useState<{ msg: string; severity: 'success' | 'error' } | null>(null);

  // Pagination state (server-side)
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [total, setTotal] = useState(0);

  // Search and filter state. Search is debounced so we don't hit the API on
  // every keystroke (pagination is server-side).
  const [searchText, setSearchText] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [userTypeFilter, setUserTypeFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilterKey>('all');

  // Active/inactive totals for the status switch (respect search + type filter)
  const [statusCounts, setStatusCounts] = useState({ active: 0, inactive: 0 });

  // Row selection + dialogs
  const selection = useRowSelection();
  const [deleteTarget, setDeleteTarget] = useState<User | null>(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchText), 400);
    return () => clearTimeout(timer);
  }, [searchText]);

  // Fetch users
  const fetchUsers = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const params: UserSearchParams = {
        page: page + 1, // API expects 1-based page
        size: pageSize,
        search: debouncedSearch || undefined,
        user_type: userTypeFilter || undefined,
        enabled: statusFilter === 'all' ? undefined : statusFilter === 'active'
      };

      const response = await userAPI.getUsers(params);
      setUsers(response.users);
      setTotal(response.total);
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Failed to fetch users');
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, debouncedSearch, userTypeFilter, statusFilter]);

  // Status counts: two size-1 queries piggyback on the server's `total`
  const fetchStatusCounts = useCallback(async () => {
    try {
      const base = {
        page: 1,
        size: 1,
        search: debouncedSearch || undefined,
        user_type: userTypeFilter || undefined,
      };
      const [active, inactive] = await Promise.all([
        userAPI.getUsers({ ...base, enabled: true }),
        userAPI.getUsers({ ...base, enabled: false }),
      ]);
      setStatusCounts({ active: active.total, inactive: inactive.total });
    } catch {
      /* switch falls back to stale counts; the list itself reports errors */
    }
  }, [debouncedSearch, userTypeFilter]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  useEffect(() => {
    fetchStatusCounts();
  }, [fetchStatusCounts]);

  // Reset to the first page when any filter changes
  useEffect(() => {
    setPage(0);
  }, [debouncedSearch, userTypeFilter, statusFilter]);

  const refresh = () => {
    fetchUsers();
    fetchStatusCounts();
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await userAPI.deleteUser(deleteTarget.UserID);
      setSnackbar({ msg: t('users.deleted', 'User deleted'), severity: 'success' });
      setDeleteTarget(null);
      refresh();
    } catch (err: any) {
      setSnackbar({ msg: err?.response?.data?.detail || t('users.deleteFailed', 'Failed to delete user'), severity: 'error' });
    } finally {
      setDeleting(false);
    }
  };

  const handleToggleStatus = async (user: User) => {
    try {
      await userAPI.toggleUserStatus(user.UserID);
      setSnackbar({
        msg: user.is_enabled
          ? t('users.disabledSuccess', 'User disabled')
          : t('users.enabledSuccess', 'User enabled'),
        severity: 'success',
      });
      refresh();
    } catch (err: any) {
      setSnackbar({ msg: err?.response?.data?.detail || t('users.toggleFailed', 'Failed to change user status'), severity: 'error' });
    }
  };

  // -------------------------------------------------------------------------
  // Shared render fragments
  // -------------------------------------------------------------------------

  const userAvatar = (user: User, size = 34) => {
    const name = user.full_name || user.UserName || '?';
    const initials = user.FirstName && user.LastName
      ? `${user.FirstName[0]}${user.LastName[0]}`
      : name[0]?.toUpperCase() || '?';
    const avatarUrl = user.ProfileImage
      ? `${config.apiBaseUrl}/files/user-avatars/${user.ProfileImage}`
      : undefined;
    const colorIndex = name.split('').reduce((acc: number, c: string) => acc + c.charCodeAt(0), 0) % AVATAR_COLORS.length;
    return (
      <Avatar
        src={avatarUrl}
        alt={`${name} avatar`}
        sx={{ width: size, height: size, bgcolor: AVATAR_COLORS[colorIndex], fontSize: size * 0.4 }}
      >
        {initials}
      </Avatar>
    );
  };

  const roleChip = (user: User) => (
    <Chip
      label={user.user_type_name || getUserTypeLabel(user.UserType)}
      size="small"
      color={getUserTypeColor(user.UserType)}
      variant={user.UserType === '01' ? 'filled' : 'outlined'}
    />
  );

  const nameCell = (user: User, size?: number) => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 0 }}>
      {userAvatar(user, size)}
      <NameCell name={user.full_name || user.UserName} subtitle={`@${user.UserName}`} />
    </Box>
  );

  const statusDot = (user: User) => (
    <StatusDot active={user.is_enabled} activeLabel={t('common.active', 'Active')} inactiveLabel={t('common.inactive', 'Inactive')} />
  );

  const actionsFor = (user: User) => {
    if (!userCanManage) return { menu: [], strip: [] };
    const edit: RowAction = {
      key: 'edit',
      label: t('common.edit', 'Edit'),
      icon: <EditIcon fontSize="small" />,
      onClick: () => navigate(`/users/${user.UserID}/edit`),
    };
    const password: RowAction = {
      key: 'password',
      label: t('users.changePassword', 'Change Password'),
      icon: <LockIcon fontSize="small" />,
      onClick: () => navigate(`/users/${user.UserID}/change-password`),
    };
    const toggle: RowAction = {
      key: 'toggle',
      label: user.is_enabled ? t('users.disableUser', 'Disable') : t('users.enableUser', 'Enable'),
      icon: user.is_enabled ? <PersonOffIcon fontSize="small" /> : <PersonIcon fontSize="small" />,
      onClick: () => handleToggleStatus(user),
    };
    const remove: RowAction = {
      key: 'delete',
      label: t('common.delete', 'Delete'),
      icon: <DeleteIcon fontSize="small" />,
      color: 'error',
      onClick: () => setDeleteTarget(user),
    };
    return { menu: [edit, password, toggle, remove], strip: [edit, password] };
  };

  const detailStrip = (user: User) => (
    <DetailStrip
      fields={[
        { label: t('users.jobTitle', 'Job Title'), value: user.JobTitle },
        { label: t('users.mobile', 'Mobile'), value: user.Mobile },
        { label: t('users.phone', 'Phone'), value: user.Phone },
        // Company is a column on desktop
        { label: t('users.company', 'Company'), value: isMobile && userIsSuper ? user.CompanyName : null },
      ]}
      description={user.EmailAddress || t('users.noEmail', 'No email address')}
      actions={actionsFor(user).strip}
    />
  );

  const emptyState = (
    <EmptyState
      title={t('users.noUsers')}
      description={t('users.noUsersDescription')}
      icon={<SearchIcon />}
      actionLabel={userCanCreate ? t('users.newUser') : undefined}
      onAction={userCanCreate ? () => navigate('/users/new') : undefined}
    />
  );

  const paginationProps = {
    rowsPerPageOptions: [10, 25, 50, 100],
    component: 'div' as const,
    count: total,
    rowsPerPage: pageSize,
    page,
    onPageChange: (_: unknown, newPage: number) => setPage(newPage),
    onRowsPerPageChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setPageSize(parseInt(e.target.value, 10));
      setPage(0);
    },
  };

  const columnCount = userIsSuper ? 6 : 5;

  if (loading && users.length === 0 && total === 0 && !error) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={400}>
        <CircularProgress />
      </Box>
    );
  }

  const filterSummary =
    debouncedSearch || userTypeFilter || statusFilter !== 'all'
      ? `Showing ${users.length} of ${total} users (filtered)`
      : `Total users: ${total}`;

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <ListToolbar
        search={searchText}
        onSearch={setSearchText}
        searchPlaceholder={t('users.searchPlaceholder')}
        statusOptions={[
          { key: 'all', label: t('users.allUsers', 'All Users'), count: statusCounts.active + statusCounts.inactive },
          { key: 'active', label: t('common.active', 'Active'), count: statusCounts.active },
          { key: 'inactive', label: t('common.inactive', 'Inactive'), count: statusCounts.inactive },
        ]}
        status={statusFilter}
        onStatus={setStatusFilter}
        onRefresh={refresh}
        loading={loading}
        addLabel={userCanCreate ? t('users.newUser') : undefined}
        onAdd={() => navigate('/users/new')}
        filters={
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel id="user-type-filter-label">{t('users.userType')}</InputLabel>
            <Select
              labelId="user-type-filter-label"
              value={userTypeFilter}
              label={t('users.userType')}
              onChange={(e: any) => setUserTypeFilter(e.target.value)}
            >
              <MenuItem value="">{t('users.allTypes')}</MenuItem>
              {filterableTypes.map(ft => (
                <MenuItem key={ft.value} value={ft.value}>{ft.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
        }
      />

      {/* Mobile card list */}
      {isMobile ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {users.length === 0 && !loading ? (
            <Paper sx={{ p: 3 }}>{emptyState}</Paper>
          ) : (
            users.map((user) => (
              <SelectableCard
                key={user.UserID}
                rowRef={selection.rowRef(user.UserID)}
                selected={selection.selectedId === user.UserID}
                onClick={() => selection.toggle(user.UserID)}
                header={
                  <>
                    <Box sx={{ flex: 1, minWidth: 0 }}>{nameCell(user, 38)}</Box>
                    {statusDot(user)}
                    <RowActionsMenu actions={actionsFor(user).menu} />
                  </>
                }
                body={roleChip(user)}
                detail={detailStrip(user)}
              />
            ))
          )}
          <TablePagination
            {...paginationProps}
            labelRowsPerPage=""
            sx={{ '& .MuiTablePagination-selectLabel': { display: 'none' } }}
          />
        </Box>
      ) : (
        /* Desktop table: click a row to select it and show its detail strip */
        <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={HEADER_CELL_SX}>{t('users.fullName', 'User')}</TableCell>
                <TableCell sx={HEADER_CELL_SX}>{t('users.email', 'Email')}</TableCell>
                <TableCell sx={HEADER_CELL_SX}>{t('users.userType', 'Role')}</TableCell>
                {userIsSuper && <TableCell sx={HEADER_CELL_SX}>{t('users.company', 'Company')}</TableCell>}
                <TableCell sx={{ ...HEADER_CELL_SX, width: 110 }}>{t('common.status', 'Status')}</TableCell>
                <TableCell sx={{ ...HEADER_CELL_SX, width: 48 }} />
              </TableRow>
            </TableHead>
            <TableBody>
              {users.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={columnCount} align="center" sx={{ py: 4 }}>
                    {loading ? (
                      <Typography color="text.secondary">{t('common.loading')}</Typography>
                    ) : (
                      emptyState
                    )}
                  </TableCell>
                </TableRow>
              ) : (
                users.map((user) => (
                  <SelectableTableRow
                    key={user.UserID}
                    rowRef={selection.rowRef(user.UserID)}
                    selected={selection.selectedId === user.UserID}
                    onClick={() => selection.toggle(user.UserID)}
                    colSpan={columnCount}
                    detail={detailStrip(user)}
                  >
                    <TableCell sx={{ maxWidth: 300 }}>{nameCell(user)}</TableCell>
                    <TableCell>
                      <Typography variant="body2" noWrap sx={{ color: user.EmailAddress ? 'text.primary' : 'text.disabled' }}>
                        {user.EmailAddress || '—'}
                      </Typography>
                    </TableCell>
                    <TableCell>{roleChip(user)}</TableCell>
                    {userIsSuper && (
                      <TableCell>
                        <Typography variant="body2" noWrap sx={{ color: user.CompanyName ? 'text.primary' : 'text.disabled' }}>
                          {user.CompanyName || '—'}
                        </Typography>
                      </TableCell>
                    )}
                    <TableCell>{statusDot(user)}</TableCell>
                    <TableCell align="right" sx={{ pr: 1 }}>
                      <RowActionsMenu actions={actionsFor(user).menu} />
                    </TableCell>
                  </SelectableTableRow>
                ))
              )}
            </TableBody>
          </Table>
          {/* Footer: filter summary on the left, pagination on the right */}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', pl: 2 }}>
            <Typography variant="body2" color="text.secondary">
              {filterSummary}
              {userTypeFilter && <span> &bull; {t('users.userType')}: {getUserTypeLabel(userTypeFilter)}</span>}
            </Typography>
            <TablePagination {...paginationProps} labelRowsPerPage={t('common.rowsPerPage')} />
          </Box>
        </TableContainer>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteTarget !== null}
        onClose={() => !deleting && setDeleteTarget(null)}
        maxWidth="xs"
        fullWidth
        disableEscapeKeyDown={deleting}
      >
        <DialogTitle>{t('users.deleteTitle')}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('users.deleteConfirmBody', {
              defaultValue:
                'Delete user "{{name}}"? The account will be disabled and they will no longer be able to log in.',
              name: deleteTarget?.full_name || deleteTarget?.UserName || '',
            })}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)} disabled={deleting}>
            {t('common.cancel')}
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
            disabled={deleting}
            startIcon={deleting ? <CircularProgress size={18} color="inherit" /> : <DeleteIcon />}
          >
            {deleting ? t('users.deleting', 'Deleting...') : t('common.delete')}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbar !== null} autoHideDuration={4000} onClose={() => setSnackbar(null)}>
        <Alert
          severity={snackbar?.severity ?? 'success'}
          onClose={() => setSnackbar(null)}
          variant="filled"
        >
          {snackbar?.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default UsersPage;
